// Vista principal del control diario: muestra indicadores del día y una tabla con los cobros,
// montos y estados de liquidación de cada usuario. Permite cambiar la fecha, filtrar,
// ordenar, paginar y descargar en CSV; al expandir una fila muestra DailySettlementCharges.
import React, { useEffect, useState } from 'react'
import { Info, Users, WarningCircle } from '@phosphor-icons/react'
import SortableDataTable from '../../components/table/SortableDataTable'
import TableExpandButton from '../../components/table/TableExpandButton'
import AppChip from '../../components/chips/AppChip'
import UbiquaTablePagination from '../../components/table/UbiquaTablePagination'
import FilterUbiquaMui from '../../components/Filters/FilterUbiquaMui/FilterUbiquaMui'
import { buildSelectValue, getSelectValues } from '../../components/Filters/filterSelectState'
import useDailySettlementControl from './useDailySettlementControl'
import { exportDailySettlementControlCsv } from './helpers/exportDailySettlementControlCsv'
import { formatMoney } from '../../helpers/utils'
import {
  STATUS_LABELS,
  STATUS_VARIANTS,
  DAILY_CONTROL_TILES,
  DAILY_CONTROL_TEXT,
  DAILY_CONTROL_CONFIG,
  DAILY_CONTROL_COLUMNS,
  DAILY_CONTROL_FILTERS
} from './constants/dailySettlementControlConstants'
import DailySettlementCharges from './DailySettlementCharges'
import UbiquaTowerToastAlert from '../../components/alerts/UbiquaTowerToastAlert'
import { UI_COLOR_ERROR_MATERIAL, UI_COLOR_ERROR_MATERIAL_BG } from '../../constants/uiColors'
import SingleDayNavigator from './SingleDayNavigator'
import SummaryKpiTile from './SummaryKpiTile'
import TableEmptyState from './TableEmptyState'
import LoadingScreen from '../../components/LoadingScreen'
import { SETTLEMENT_INDEX_TEXT } from '../constants/constants'
import './dailySettlementControl.scss'

export default function DailySettlementControlTab({ canDownload = false }) {
  const control = useDailySettlementControl()
  const { params, data, loading, error, expandedUid } = control
  const [errorAlertOpen, setErrorAlertOpen] = useState(true)
  useEffect(() => setErrorAlertOpen(true), [error])
  const summary = data?.summary
  const totalCount = data?.count || 0
  const perPage = data?.per_page || params.per_page
  const date = params.date || data?.date
  const noCharges = summary?.charges_count === 0
  const filtered = Boolean(params.q || params.status !== 'all' || params.sort_by)
  const columnRenderers = {
    expand: (row) =>
      row.charges_count > 0 ? (
        <TableExpandButton
          label={DAILY_CONTROL_TEXT.EXPAND_USER(row.user_full_name)}
          expanded={expandedUid === row.user_uid}
          onClick={() => control.setExpandedUid(expandedUid === row.user_uid ? null : row.user_uid)}
        />
      ) : null,
    user_uid: (row) => (
      <span className="ubiqua-identity-stacked">
        <span className="ubiqua-identity-stacked__name" title={row.user_full_name}>
          {row.user_full_name || DAILY_CONTROL_TEXT.MISSING_VALUE}
        </span>
        <span className="ubiqua-identity-stacked__code ubiqua-code" title={row.user_uid}>
          {row.user_uid || DAILY_CONTROL_TEXT.MISSING_VALUE}
        </span>
      </span>
    ),
    charges_count: (row) => row.charges_count || DAILY_CONTROL_TEXT.MISSING_VALUE,
    collected_amount: (row) =>
      row.charges_count > 0
        ? formatMoney(row.collected_amount || 0)
        : DAILY_CONTROL_TEXT.MISSING_VALUE,
    pending_charges_count: (row) => row.pending_charges_count || DAILY_CONTROL_TEXT.MISSING_VALUE,
    pending_amount: (row) =>
      row.pending_charges_count > 0 ? (
        <span className="daily-control-pending">{formatMoney(row.pending_amount || 0)}</span>
      ) : (
        DAILY_CONTROL_TEXT.MISSING_VALUE
      ),
    status: (row) => (
      <AppChip label={STATUS_LABELS[row.status]} variant={STATUS_VARIANTS[row.status]} />
    )
  }
  const columns = DAILY_CONTROL_COLUMNS.map((column) => ({
    ...column,
    render: columnRenderers[column.key]
  }))

  if (loading && !control.hasLoaded) {
    return (
      <LoadingScreen
        text={SETTLEMENT_INDEX_TEXT.DAILY_LOADING}
        minHeight={DAILY_CONTROL_CONFIG.LOADING_MIN_HEIGHT}
      />
    )
  }

  return (
    <section
      className="daily-settlement-control app-content-enter"
      aria-label={DAILY_CONTROL_TEXT.ARIA_LABEL}
    >
      <FilterUbiquaMui
        title={DAILY_CONTROL_TEXT.FILTER_TITLE}
        headingTrailingContent={
          <span className="daily-control-notice">
            <Info size={14} aria-hidden="true" />
            {DAILY_CONTROL_TEXT.NOTICE}
          </span>
        }
        filters={DAILY_CONTROL_FILTERS}
        appliedFilterSelections={{
          status: buildSelectValue(params.status === 'all' ? [] : [params.status])
        }}
        autoApply
        showMoreFilters={false}
        searchInput={control.searchInput}
        appliedSearchInput={params.q}
        searchPlaceholder={DAILY_CONTROL_TEXT.SEARCH_PLACEHOLDER}
        isAnyFilterSelected={filtered}
        onSearchChange={(event) => control.changeSearch(event.target.value)}
        onSearchEnter={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            control.submitSearch(control.searchInput)
          }
        }}
        onSearchClear={() => {
          control.changeSearch('')
          control.submitSearch('')
        }}
        onFilterChange={(_, selection) =>
          control.update({ status: getSelectValues(selection)[0] || 'all' })
        }
        onResetFilters={control.reset}
        showExport={canDownload}
        onExport={() => exportDailySettlementControlCsv(data?.result || [], date)}
        exportTooltip={DAILY_CONTROL_TEXT.EXPORT}
        exportAriaLabel={DAILY_CONTROL_TEXT.EXPORT}
        exportDisabled={loading || !data?.result?.length}
        searchTrailingAction={
          <SingleDayNavigator date={date} today={data?.today} onChange={control.changeDate} />
        }
      />

      {error && (
        <UbiquaTowerToastAlert
          open={errorAlertOpen}
          title="Error al cargar el control diario"
          message={error}
          icon={<WarningCircle size={18} aria-hidden="true" />}
          colors={{ iconBackground: UI_COLOR_ERROR_MATERIAL_BG, progress: UI_COLOR_ERROR_MATERIAL }}
          onClose={() => setErrorAlertOpen(false)}
        />
      )}
      {summary && (
        <div
          className={`daily-control-kpis${loading ? ' daily-control-kpis--updating' : ''}`}
          aria-label={DAILY_CONTROL_TEXT.SUMMARY_ARIA_LABEL}
          aria-busy={loading}
        >
          {loading && (
            <span className="visually-hidden" role="status">
              {DAILY_CONTROL_TEXT.UPDATING}
            </span>
          )}
          <SummaryKpiTile
            label={DAILY_CONTROL_TEXT.TOTAL_USERS}
            icon={Users}
            tone="neutral"
            value={summary.users_count}
            support={`${formatMoney(summary.collected_amount || 0)} ${DAILY_CONTROL_TEXT.COLLECTED}`}
          />
          {DAILY_CONTROL_TILES.map((tile) => (
            <SummaryKpiTile
              key={tile.status}
              {...tile}
              value={summary.states[tile.status].users_count}
              support={
                tile.status === 'no_charges'
                  ? DAILY_CONTROL_TEXT.NO_CHARGES
                  : `${formatMoney(summary.states[tile.status].amount || 0)} ${tile.status === 'settled' ? DAILY_CONTROL_TEXT.SETTLED : DAILY_CONTROL_TEXT.PENDING}`
              }
              selected={params.status === tile.status}
              onClick={() =>
                control.update({ status: params.status === tile.status ? 'all' : tile.status })
              }
            />
          ))}
        </div>
      )}
      {!error && (
        <>
          <SortableDataTable
            ariaLabel={DAILY_CONTROL_TEXT.TABLE_ARIA_LABEL}
            columns={columns}
            rows={data?.result || []}
            rowKey={DAILY_CONTROL_CONFIG.ROW_KEY}
            minWidth={DAILY_CONTROL_CONFIG.MIN_WIDTH}
            sortBy={params.sort_by}
            sortDirection={params.sort_direction}
            onSort={control.sort}
            loading={loading}
            emptyContent={<TableEmptyState noCharges={noCharges && !filtered} />}
            isRowExpanded={(row) => row.user_uid === expandedUid}
            expandedRowColumnOffset={DAILY_CONTROL_CONFIG.EXPANDED_COLUMN_OFFSET}
            renderExpandedRow={(row) => (
              <DailySettlementCharges date={date} userUid={row.user_uid} cache={control.chargesCache} />
            )}
          />
          {totalCount > DAILY_CONTROL_CONFIG.PAGINATION_THRESHOLD && (
            <UbiquaTablePagination
              ariaLabel={DAILY_CONTROL_TEXT.PAGINATION_ARIA_LABEL}
              itemLabel={DAILY_CONTROL_TEXT.ITEM_LABEL}
              disabled={loading}
              pagination={{
                total_count: totalCount,
                per_page: perPage,
                total_pages: Math.ceil(totalCount / perPage),
                current_page: params.page
              }}
              onPageChange={(_, page) => control.update({ page })}
              onRowsPerPageChange={(_, size) => control.update({ per_page: size })}
            />
          )}
        </>
      )}
    </section>
  )
}
